'use client';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { LoadingSpinner } from '@osmosis/tailwind-ui/components/LoadingSpinner';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { FormattedMessage } from 'react-intl';
import Divider from '../Divider';
import Link from 'next/link';
import React, { useCallback, useEffect, useState } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { useFetch } from '../../context/FetchProvider';
import { useLanguageContext } from '../../context/LanguageProvider';
import { PlaylistSearchHitType } from '../../types/search';
import { renderSearchHits } from '../../app/_lib/frontend/retired-page';
import { bg0, bg2 } from '@osmosis/tailwind-ui/styles/background';
import { text2, textRed } from '@osmosis/tailwind-ui/styles/text';
import { SEARCH_PATH, SEARCH_TERM } from '../../constants/search';

type RetiredContentModalProps = {
  retiredPageTitle: string;
};

/**
 * RetiredPagePhase2.
 */
const RetiredPagePhase2 = (props: RetiredContentModalProps) => {
  const { retiredPageTitle } = props;

  const debouncedValue = useDebounce(retiredPageTitle, 700);
  const [isLoading, setLoading] = useState(false);
  const { get } = useFetch();
  const { activeLanguage } = useLanguageContext();
  const [searchHits, setSearchHits] = useState<PlaylistSearchHitType[]>([]);

  const formattedValue = retiredPageTitle?.trim().replace(/ /g, '_') || '';
  const searchUrl = `${SEARCH_PATH}?${new URLSearchParams({
    [SEARCH_TERM]: formattedValue,
  })}`;

  const search = useCallback(
    async (val: string) => {
      if (!val) {
        setLoading(false);

        return;
      }

      setLoading(true);

      const { data } = await get<{ hits: PlaylistSearchHitType[] }>(
        '/data/search',
        {
          params: {
            q: val,
            filter: undefined,
            watchStatus: true,
            language: activeLanguage,
            searchFilters: ['subProducts::Clinical Sciences'],
          },
        },
      );

      const { hits } = data;

      setSearchHits(hits);

      if (data) setLoading(false);
    },
    [retiredPageTitle],
  );

  useEffect(() => {
    search(debouncedValue);
  }, []);

  const renderSearchResults = () => {
    return (
      <>
        {isLoading ? (
          <Flex className="h-full w-full items-center justify-center">
            <LoadingSpinner />
          </Flex>
        ) : (
          <Box className="w-full p-6">{renderSearchHits(searchHits)}</Box>
        )}
      </>
    );
  };

  return (
    <Flex
      className={`mx-auto flex-col items-center justify-center pb-6 ${bg0} h-full sm:w-full md:w-full lg:w-1/2`}
      style={{ zIndex: 999 }}
    >
      <Flex className="items-left mt-7 flex-col pl-6 pr-6">
        <Box>
          <Flex>
            <H3>{retiredPageTitle}</H3>
            <H3 className={`ml-1.5 ${text2}`}>
              <FormattedMessage
                id="retired-modal.phase_2_title"
                defaultMessage="(Retired)"
              />
            </H3>
          </Flex>

          <H4 className={`${textRed} mt-1`}>
            <FormattedMessage
              id="retired-modal.phase_2_description"
              defaultMessage="This video has been retired."
            />
          </H4>
        </Box>
        <Box className="mt-2">
          <Divider className={`${bg2}`} />
        </Box>
        <Box className="mt-2">
          <H4>
            <FormattedMessage
              id="retired-modal.phase_2_info"
              defaultMessage="We're sorry for the inconvenience. You cannot access the video anymore. We've expanded our collection to include new videos based on the latest evidence-based guidelines which provide a relevant and high-yield learning experience! Here are some new related videos."
            />
          </H4>
        </Box>
      </Flex>
      {renderSearchResults()}
      {!isLoading && (
        <Box>
          <Link
            data-testid="retired-modal.phase_2.search_for_another_video"
            href={'#'}
            onClick={() => {
              window.location.href = searchUrl;
            }}
          >
            <P className="text-center font-semibold text-blue-500">
              <FormattedMessage
                id="retired-modal.phase_2.search_for_another_video"
                defaultMessage="Didn't find the video you're looking for? Search for another video"
              />
            </P>
          </Link>
          <P className="text-center font-semibold">
            <FormattedMessage
              id="retired-modal.phase_2.reach_out_text"
              defaultMessage="Or, reach out to customer support in the chat bubble, in accessing the most updated videos (eg. Updating your playlist)."
            />
          </P>
        </Box>
      )}
    </Flex>
  );
};

export default RetiredPagePhase2;
